import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    List,
    Box,
    Typography,
    Avatar,
    CircularProgress,
    Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface FacebookPage {
    page_id: string;
    page_name: string;
    page_username: string | null;
    page_category: string;
    page_profile_picture: string;
    page_access_token: string;
    followers_count: number;
    is_verified: boolean;
}

interface FacebookPageSelectionModalProps {
    open: boolean;
    onClose: () => void;
    pages: FacebookPage[];
    onSelectPage: (page: FacebookPage) => void;
    connectingPageId: string | null;
}

const FacebookPageSelectionModal: React.FC<FacebookPageSelectionModalProps> = ({
    open,
    onClose,
    pages,
    onSelectPage,
    connectingPageId
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 'var(--large-border-radius)',
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: "26px", fontWeight: 700, fontFamily: "var(--font-family-manrope)", color: "#111827", lineHeight: 1.3 }}>
                        Select Facebook Page
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Choose a page to connect to your business
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={!!connectingPageId}
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <List sx={{ py: 0 }}>
                    {pages.map((page, index) => {
                        const isConnecting = connectingPageId === page.page_id;
                        const isDisabled = !!connectingPageId;

                        return (
                            <Box
                                key={page.page_id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 2,
                                    px: 3,
                                    borderBottom: index !== pages.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    '&:hover': {
                                        backgroundColor: '#f9f9f9'
                                    }
                                }}
                            >
                                {/* Left side: Avatar + Page Info */}
                                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    <Avatar
                                        src={page.page_profile_picture}
                                        alt={page.page_name}
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            mr: 2,
                                            border: '2px solid #e0e0e0'
                                        }}
                                    />

                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={500}>
                                                {page.page_name}
                                            </Typography>
                                            {page.is_verified && (
                                                <CheckCircleIcon
                                                    sx={{ fontSize: 18, color: '#1877f2' }}
                                                />
                                            )}
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {page.page_category}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {page.followers_count.toLocaleString()} followers
                                            </Typography>
                                            {page.page_username && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">
                                                        •
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        @{page.page_username}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Right side: Connect Button */}
                                <Button
                                    variant="contained"
                                    size="medium"
                                    disabled={isDisabled}
                                    onClick={() => onSelectPage(page)}
                                    sx={{
                                        ml: 2,
                                        minWidth: 120,
                                        backgroundColor: '#1877f2',
                                        color: '#fff',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#166fe5',
                                            boxShadow: 'none'
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: isConnecting ? '#1877f2' : '#e0e0e0',
                                            color: isConnecting ? '#fff' : '#999'
                                        }
                                    }}
                                    startIcon={isConnecting && (
                                        <CircularProgress
                                            size={16}
                                            thickness={4}
                                            sx={{ color: '#fff' }}
                                        />
                                    )}
                                >
                                    {isConnecting ? 'Connecting...' : 'Connect'}
                                </Button>
                            </Box>
                        );
                    })}
                </List>

                {pages.length === 0 && (
                    <Box
                        sx={{
                            py: 6,
                            px: 3,
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            No Facebook pages found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Make sure you have admin access to at least one Facebook page
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default FacebookPageSelectionModal;
